'use client';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';

import { Svg } from '@/components';
import { debounce } from '@/utils/helper';
import { ChevronRightIcon } from '@/icons';
import { fonts } from '@/config';
import { useCleverTap } from '@/context/clevertapContext';
import { usePathname } from 'next/navigation';
import PLPBanner from '@/components/PLP/Banner';
import Drawer from '@/layouts/Drawer/Drawer';
import { useHeaderContext } from '@/context/headerContext';

export type CategoriesListProps = {
  id: number;
  name: string;
  url_key: string;
  children: Array<CategoriesListProps>;
  children_count: string;
  image: string;
  banner1_image?: string | null;
  banner1_title?: string | null;
  banner1_url?: string | null;
  banner1_urltext?: string | null;
};

const HoverDropdown: FC = () => {
  const pathname = usePathname();
  const { pushEvent } = useCleverTap();
  const { showHoverDropdown: show, toggleHoverDropdown: onClose } =
    useHeaderContext();
  const { hoverCategory, categoriesToShowOnHover: categories } =
    useHeaderContext();
  const [lvlOne, setLvlOne] = useState(categories[0] || {});
  const [lvlTwo, setLvlTwo] = useState(categories[0]?.children[0] || {});
  const [lvlThree, setLvlThree] = useState(
    categories[0]?.children[0]?.children[0] || {}
  );
  const [lvlFour, setLvlFour] = useState(
    categories[0]?.children[0]?.children[0]?.children[0] || {}
  );
  const focusRef = useRef(null);

  const debouncedSelectOne = debounce(setLvlOne, 290);
  const debouncedSelectTwo = debounce(setLvlTwo, 290);
  const debouncedSelectThree = debounce(setLvlThree, 290);
  const debouncedSelectFour = debounce(setLvlFour, 290);

  const BASE_IMAGE_URL = `${process.env.NEXT_PUBLIC_CDN_LINK}/media/catalog/category/`;

  const findFirstLvl = () => {
    return categories?.find(em => em?.id === lvlOne?.id);
  };

  const findSecondLvl = () => {
    return categories
      ?.find(em => em?.id === lvlOne?.id)
      ?.children?.find(ed => ed?.id === lvlTwo?.id);
  };

  const findThirdLvl = () => {
    return categories
      ?.find(em => em?.id === lvlOne?.id)
      ?.children?.find(ed => ed?.id === lvlTwo?.id)
      ?.children?.find(el => el?.id === lvlThree?.id);
  };

  const handleClose = (category?: CategoriesListProps) => {
    if (category) {
      pushEvent({
        event: 'Category Viewed',
        data: {
          'Category Name': category?.name,
          'Category ID': category?.id,
          'Category URL Key': category?.url_key,
          'Category Path': pathname,
        },
      });
      pushEvent({
        event: 'Hover Category Clicked',
        data: {
          'Category Name': category?.name,
          'Category ID': category?.id,
          'Category URL Key': category?.url_key,
          Pathname: pathname,
        },
      });
    }
    onClose(false);
  };

  const closeCallback = useCallback(() => {
    if (show) {
      onClose(false);
    }
  }, [focusRef, show, onClose]);

  useEffect(() => {
    setLvlOne(categories[0] || {});
    setLvlTwo(categories[0]?.children[0] || {});
    setLvlThree(categories[0]?.children[0]?.children[0] || {});
    setLvlFour(categories[0]?.children[0]?.children[0]?.children[0] || {});
  }, [categories?.length]);

  useEffect(() => {
    const firstLevel = findFirstLvl();
    if (firstLevel) {
      setLvlTwo(firstLevel?.children[0] || {});
    }
  }, [lvlOne]);

  useEffect(() => {
    const secondLevel = findSecondLvl();
    if (secondLevel) {
      setLvlThree(secondLevel?.children[0] || {});
    }
  }, [lvlTwo]);

  useEffect(() => {
    const thirdLevel = findThirdLvl();
    if (thirdLevel) {
      setLvlFour(thirdLevel?.children[0] || {});
    }
  }, [lvlThree]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose(false);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = 'auto';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [show, onClose]);

  return (
    <Drawer isDrawerOpen={show} closeCallback={closeCallback}>
      <div
        className='mx-auto grid w-full max-w-7xl grid-cols-4 place-items-start justify-items-center gap-x-4 border-t border-dew'
        onClick={closeCallback}>
        {/*L1 Categories */}
        <div className='flex h-full w-full justify-between'>
          <div className='flex w-full flex-col gap-y-6 py-4 pr-16'>
            {categories.map(el => (
              <Link
                href={`/${hoverCategory}${
                  hoverCategory === 'gifting' ? '/all-gifts' : ''
                }/${el?.url_key}`}
                prefetch={false}
                onClick={() => handleClose(el)}
                className='group flex items-center justify-between outline-hidden'
                key={el?.id}
                onMouseEnter={() => debouncedSelectOne(el)}
                onMouseLeave={() => debouncedSelectOne(lvlOne)}>
                <div
                  className={twMerge(
                    'font-gilroy cursor-pointer text-lg font-semibold text-[#38383A99] hover:text-fig',
                    lvlOne?.id === el?.id && 'text-fig'
                  )}>
                  {el?.name}
                </div>
                <Svg
                  className={twMerge(
                    'invisible h-4 w-4 fill-none stroke-fig delay-300 ease-in',
                    Number(findFirstLvl()?.children_count) > 0 &&
                      'group-hover:visible',
                    lvlOne?.id === el?.id &&
                      Number(findFirstLvl()?.children_count) > 0 &&
                      'visible'
                  )}
                  viewBox='0 0 24 24'>
                  <ChevronRightIcon />
                </Svg>
              </Link>
            ))}
          </div>
          <div className='border-r border-r-[#EDEAE9]' />
        </div>
        {/* L2 Categories */}
        <div className='flex h-full w-full justify-between'>
          <div className='flex w-full flex-col gap-y-6 py-4 pr-16'>
            <div className='font-gilroy cursor-pointer text-sm font-semibold text-[#38383A]'>
              {(Number(findFirstLvl()?.children_count) > 0 &&
                findFirstLvl()?.name) ||
                ''}
            </div>
            {findFirstLvl()?.children?.map(el => {
              return (
                <Link
                  href={`/${hoverCategory}${
                    hoverCategory === 'gifting' ? '/all-gifts' : ''
                  }/${findFirstLvl()?.url_key}/${el?.url_key}`}
                  prefetch={false}
                  onClick={() => handleClose(el)}
                  className='group flex items-center justify-between'
                  key={el?.id}
                  onMouseEnter={() => debouncedSelectTwo(el)}
                  onMouseLeave={() => debouncedSelectTwo(lvlTwo)}>
                  <div
                    className={twMerge(
                      'font-gilroy cursor-pointer text-sm text-[#38383A99] hover:text-fig',
                      lvlTwo?.id === el?.id && 'text-fig'
                    )}>
                    {el?.name}
                  </div>
                  <Svg
                    className={twMerge(
                      'invisible h-3.5 w-3.5 fill-none stroke-fig delay-300 ease-in',
                      Number(findSecondLvl()?.children_count) > 0 &&
                        'group-hover:visible',
                      lvlTwo?.id === el?.id &&
                        Number(findSecondLvl()?.children_count) > 0 &&
                        'visible'
                    )}
                    viewBox='0 0 24 24'>
                    <ChevronRightIcon />
                  </Svg>
                </Link>
              );
            })}
            {Number(findFirstLvl()?.children_count) > 0 && (
              <Link
                href={`/${hoverCategory}${
                  hoverCategory === 'gifting' ? '/all-gifts' : ''
                }/${findFirstLvl()?.url_key}`}
                prefetch={false}
                onClick={() => handleClose(findFirstLvl())}
                className='font-gilroy cursor-pointer text-sm text-fig underline'>
                View All
              </Link>
            )}
          </div>
          {Number(findFirstLvl()?.children_count) > 0 && (
            <div className='border-r border-r-[#EDEAE9]' />
          )}
        </div>
        {/* L3 Categories */}
        <div className='flex h-full w-full justify-between'>
          <div className='flex w-full flex-col gap-y-6 py-4 pr-16'>
            <div className='font-gilroy cursor-pointer text-sm font-semibold text-[#38383A]'>
              {(Number(findSecondLvl()?.children_count) > 0 &&
                findSecondLvl()?.name) ||
                ''}
            </div>
            {findSecondLvl()?.children?.map(el => (
              <Link
                href={`/${hoverCategory}${
                  hoverCategory === 'gifting' ? '/all-gifts' : ''
                }/${findFirstLvl()?.url_key}/${
                  findSecondLvl()?.url_key
                }/${el?.url_key}`}
                prefetch={false}
                onClick={() => handleClose(el)}
                className='group flex items-center justify-between'
                key={el?.id}
                onMouseEnter={() => debouncedSelectThree(el)}
                onMouseLeave={() => debouncedSelectThree(lvlThree)}>
                <div
                  className={twMerge(
                    'font-gilroy cursor-pointer text-sm text-[#38383A99] hover:text-fig',
                    lvlThree?.id === el?.id && 'text-fig'
                  )}>
                  {el?.name}
                </div>
                <Svg
                  className={twMerge(
                    'invisible h-3.5 w-3.5 fill-none stroke-fig delay-300 ease-in',
                    Number(findThirdLvl()?.children_count) > 0 &&
                      'group-hover:visible',
                    lvlThree?.id === el?.id &&
                      Number(findThirdLvl()?.children_count) > 0 &&
                      'visible'
                  )}
                  viewBox='0 0 24 24'>
                  <ChevronRightIcon />
                </Svg>
              </Link>
            ))}
            {Number(findSecondLvl()?.children_count) > 0 && (
              <Link
                href={`/${hoverCategory}${
                  hoverCategory === 'gifting' ? '/all-gifts' : ''
                }/${findFirstLvl()?.url_key}/${findSecondLvl()?.url_key}`}
                prefetch={false}
                onClick={() => handleClose(findSecondLvl())}
                className='font-gilroy cursor-pointer text-sm text-fig underline'>
                View All
              </Link>
            )}
          </div>
          {Number(findSecondLvl()?.children_count) > 0 && (
            <div className='border-r border-r-[#EDEAE9]' />
          )}
        </div>
        {/* L4 Categories / PLP Banner / Image */}
        {Number(findThirdLvl()?.children_count) > 0 ? (
          <div className='flex w-full flex-col gap-y-6 py-4 lg:pr-16'>
            <div className='font-gilroy cursor-pointer text-sm font-semibold text-[#38383A]'>
              {(Number(findThirdLvl()?.children_count) > 0 &&
                findThirdLvl()?.name) ||
                ''}
            </div>
            {findThirdLvl()?.children?.map(el => (
              <Link
                href={`/${hoverCategory}${
                  hoverCategory === 'gifting' ? '/all-gifts' : ''
                }/${findFirstLvl()?.url_key}/${
                  findSecondLvl()?.url_key
                }/${findThirdLvl()?.url_key}/${el?.url_key}`}
                prefetch={false}
                onClick={() => handleClose(el)}
                className='group flex items-center justify-between'
                key={el?.id}
                onMouseEnter={() => debouncedSelectFour(el)}
                onMouseLeave={() => debouncedSelectFour(lvlFour)}>
                <div className='font-gilroy cursor-pointer text-sm text-[#38383A99] hover:text-fig'>
                  {el?.name}
                </div>
              </Link>
            ))}
            {Number(findThirdLvl()?.children_count) > 0 && (
              <Link
                href={`/${hoverCategory}${
                  hoverCategory === 'gifting' ? '/all-gifts' : ''
                }/${findFirstLvl()?.url_key}/${
                  findSecondLvl()?.url_key
                }/${findThirdLvl()?.url_key}`}
                prefetch={false}
                onClick={() => handleClose(findThirdLvl())}
                className='font-gilroy cursor-pointer text-sm text-fig underline'>
                View All
              </Link>
            )}
          </div>
        ) : (
          <div className='h-full w-full px-4 py-4'>
            {lvlOne?.banner1_image && lvlOne?.banner1_url ? (
              <button
                className='relative h-full w-full'
                onClick={() => onClose(false)}>
                <PLPBanner
                  title={lvlOne?.banner1_title ?? null}
                  imageUrl={lvlOne?.banner1_image}
                  url={lvlOne?.banner1_url}
                  urlText={lvlOne?.banner1_urltext}
                  pathName={pathname}
                />
              </button>
            ) : lvlOne?.banner1_image && !lvlOne?.banner1_url ? (
              <div className='relative h-full w-full'>
                <PLPBanner
                  title={lvlOne?.banner1_title ?? null}
                  imageUrl={lvlOne?.banner1_image}
                  url={lvlOne?.banner1_url}
                  urlText={lvlOne?.banner1_urltext}
                  pathName={pathname}
                />
              </div>
            ) : lvlOne?.image ? (
              <div className='relative aspect-263/459 w-full max-w-65.75 select-none'>
                <Image
                  className='rounded-lg object-cover'
                  alt='banner'
                  src={BASE_IMAGE_URL + lvlOne?.image.split('/').splice(-1)}
                  fill
                  sizes='25vw'
                />
                <div className='absolute bottom-0 left-0 right-0 top-0 flex h-full w-full flex-col items-start justify-end rounded-lg bg-[rgba(256,256,256,0.5)] px-3.5 pb-16'>
                  <div
                    className={twMerge(
                      fonts?.russolo?.className,
                      'font-russolo text-3xl font-bold text-[#A92606]'
                    )}>
                    {lvlOne?.name}
                  </div>
                  <div className='font-gilroy text-base font-semibold text-[#38383A99]'>
                    Get {lvlOne?.name?.toLowerCase()} delivered to your
                    doorstep.
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default HoverDropdown;
